import { useMutation, useQuery } from 'react-query'
import {
  CreateOrganizationOnboardingRequest,
  GetOrganizationsRequest,
  GetOrganizationUserProfilesRequest,
  GetWorkspaceUserProfilesRequest,
  PublishOrganizationOnboardingRequest,
  UpdateOrganizationOnboardingRequest,
  GetOrganizationOnboardingRequest,
} from 'lib/generated/api'
import { useApiClient } from 'contexts/ApiClientContext'
import { useAuth } from 'auth'
import { useHandleApiError } from 'api/utils'

export const useGetOrganizations = (
  request: GetOrganizationsRequest,
  queryOptions = {}
) => {
  const { organizationApi } = useApiClient()
  return useQuery(
    'getOrganizations',
    () => organizationApi.getOrganizations(request),
    queryOptions
  )
}

export const organizationWorkspaceUserProfilesKey =
  'getOrganizationWorkspaceUserProfiles'
export const useGetOrganizationWorkspaceUserProfiles = (
  request: GetWorkspaceUserProfilesRequest
) => {
  const { workspaceApi } = useApiClient()
  return useQuery(
    [organizationWorkspaceUserProfilesKey, request.externalUserId],
    () => workspaceApi.getWorkspaceUserProfiles(request),
    { enabled: !!request.externalUserId && !!request.organizationId }
  )
}

export const useGetOrganizationUserProfiles = (
  request: GetOrganizationUserProfilesRequest
) => {
  const { organizationApi } = useApiClient()
  return useQuery(
    ['getOrganizationUserProfiles', request.externalUserId],
    () => organizationApi.getOrganizationUserProfiles(request),
    { enabled: !!request.organizationId && !!request.externalUserId }
  )
}

export const useCreateOrganizationOnboarding = () => {
  const { organizationApi } = useApiClient()
  return useMutation((request: CreateOrganizationOnboardingRequest) =>
    organizationApi.createOrganizationOnboarding(request)
  )
}

export const useUpdateOnboardingRecordId = () => {
  const { organizationApi } = useApiClient()
  return useMutation((request: UpdateOrganizationOnboardingRequest) =>
    organizationApi.updateOrganizationOnboarding(request)
  )
}

export const usePublishOrganizationOnboarding = () => {
  const { organizationApi } = useApiClient()
  const { updateToken } = useAuth()
  const onError = useHandleApiError({ showMessage: false })
  return useMutation(
    (request: PublishOrganizationOnboardingRequest) =>
      organizationApi.publishOrganizationOnboarding(request),
    { onSuccess: () => updateToken(-1), onError }
  )
}

export const useGetOrganizationOnboarding = (
  request: GetOrganizationOnboardingRequest
) => {
  const { organizationApi } = useApiClient()
  const onError = useHandleApiError({ showMessage: false })

  return useQuery(
    ['getOrganizationOnboarding', request.onboardingRecordId],
    () => organizationApi.getOrganizationOnboarding(request),
    { enabled: !!request.onboardingRecordId, onError }
  )
}
